import { useLocale, useTranslations } from "next-intl";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { groupBy } from "lodash";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Analytics } from "@types";
import { formatPrice, joinClassNames } from "@utils/helpers";
import { TicketItem, ComboItems, Basket } from "@features/cartDetails/types";
import { useWindowSize } from "@utils/hooks";
import { useIsShopsite } from "@context/DomainContext";
import DeleteCombo from "./deleteCombo";
import DeleteTicket from "./deleteTicket";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/accordion";
import { TicketnameInput } from "@features/ticketname";
import { Badge, Button, Card, Text } from "@components/theming";

interface Props {
  items: TicketItem[];
  comboItems: ComboItems[];
  hasOtherEvents: boolean;
  currency: Basket["Currency"];
  eventName: string;
  eventId: number;
  showingId: number;
  analyticsConfigs?: Analytics[];
  canTrack?: boolean;
}

const DisplayTickets = ({
  items,
  comboItems,
  hasOtherEvents,
  currency,
  eventName,
  eventId,
  showingId,
  analyticsConfigs,
  canTrack,
}: Props) => {
  const locale = useLocale();
  const ticketTranslations = useTranslations("Checkout.Cart.tickets");
  const isShopsite = useIsShopsite();
  const width = useWindowSize().width;

  const handleTotalComboPrice = (tickets: TicketItem[]) =>
    tickets.reduce((prev, curr) => prev + curr.Cost, 0);

  const comboTickets = comboItems.map((comboItem) => ({
    Combo: comboItem.Combo,
    ComboId: comboItem.ComboId,
    Items: comboItem.Items.map((uniqueId) =>
      items.find((item) => item.UniqueId === uniqueId)
    ).filter((item): item is TicketItem => item !== undefined),
  }));

  const nonComboTickets = items.filter(
    (item) =>
      !comboItems.some((combo) =>
        combo.Items.some((comboItem) => comboItem === item.UniqueId)
      )
  );

  const tickets_with_min = groupBy(
    items.filter((item) => item.PriceMinimum > 0),
    "PriceId"
  );

  return width && width < 768 ? (
    <ul role="list" className="mt-4 flex flex-col gap-4">
      {comboTickets.length > 0 ? (
        comboTickets.map((comboTicket, index) => (
          <Card
            key={`${comboTicket.Combo}-${index}`}
            size="2"
            shopsiteClasses={joinClassNames(
              "py-2 px-4 border bg-white",
              isShopsite ? "rounded-xl" : "rounded-[var(--radius-4)]"
            )}
          >
            <AccordionPrimitive.Root
              type="single"
              collapsible
              defaultValue={
                comboTicket.Items[0].TicketNameRequired
                  ? comboTicket.Combo
                  : undefined
              }
            >
              <AccordionPrimitive.Item
                value={comboTicket.Combo}
                className="flex flex-col items-center justify-between gap-x-6 gap-y-3"
              >
                <div className="flex flex-col gap-1 w-full text-left">
                  <Text
                    as="div"
                    weight="bold"
                    sharedClasses="flex items-center justify-between gap-2 leading-6"
                    shopsiteClasses="font-bold"
                  >
                    <span>{comboTicket.Combo}</span>
                    <DeleteCombo
                      eventName={eventName}
                      eventId={eventId}
                      priceAgeId={comboTicket.Items[0].PriceAgeId}
                      showingId={showingId}
                      type={comboTicket.Items[0].TicketType}
                      price={comboTicket.Items[0].Cost}
                      analyticsConfigs={analyticsConfigs}
                      uniqueId={comboTicket.Items[0].UniqueId}
                      canTrack={canTrack}
                      is_last_combo={
                        !hasOtherEvents
                          ? [...comboTickets, ...nonComboTickets].length === 1
                          : false
                      }
                    />
                  </Text>
                  <Text
                    as="p"
                    color="gray"
                    size="2"
                    shopsiteClasses="text-sm text-gray-500"
                  >
                    {formatPrice(
                      locale,
                      currency.CurrencyCode,
                      handleTotalComboPrice(comboTicket.Items)
                    )}
                  </Text>
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      color="green"
                      sharedClasses="w-fit"
                      shopsiteClasses="!py-1 !rounded-lg font-semibold"
                    >
                      <Text size="2" weight="medium">
                        {ticketTranslations("combos.heading")}
                      </Text>
                    </Badge>
                    <Badge
                      color="green"
                      sharedClasses="w-fit"
                      shopsiteClasses="!py-1 !rounded-lg font-medium"
                    >
                      <Text size="2" weight="medium">
                        {comboTicket.Items[0].Discount}
                      </Text>
                    </Badge>
                  </div>
                </div>
                <AccordionPrimitive.Trigger asChild>
                  <Button
                    whitelabelVariant="surface"
                    shopsiteVariant="soft"
                    sharedClasses="group flex items-center justify-center gap-1 w-full [&[data-state=open]>svg]:rotate-180"
                    shopsiteClasses="py-1 !rounded-lg !font-normal"
                  >
                    <ChevronDownIcon className="w-4 h-4 will-change-transform transition-transform ease-in-out duration-200" />
                    <span className="group-data-[state=open]:hidden">
                      {ticketTranslations("combos.showcombo")}
                    </span>
                    <span className="group-data-[state=closed]:hidden">
                      {ticketTranslations("combos.hidecombo")}
                    </span>
                  </Button>
                </AccordionPrimitive.Trigger>
                <AccordionPrimitive.Content className="flex flex-col divide-y w-full">
                  {comboTicket.Items.map((ticket) => (
                    <div
                      key={ticket.UniqueId}
                      id={`ticket-${ticket.UniqueId}`}
                      className="py-4 flex flex-col items-center justify-between gap-3"
                    >
                      <div className="flex flex-col gap-1 w-full">
                        <Text
                          as="div"
                          weight="bold"
                          sharedClasses="flex items-center justify-between gap-2 leading-6"
                          shopsiteClasses="font-bold"
                        >
                          <span>{ticket.TicketType}</span>
                        </Text>
                        <Text
                          as="p"
                          color="gray"
                          size="2"
                          shopsiteClasses="text-sm text-gray-500"
                        >
                          {formatPrice(
                            locale,
                            currency.CurrencyCode,
                            ticket.Cost
                          )}
                        </Text>
                        <Badge
                          color="sky"
                          sharedClasses="w-fit"
                          shopsiteClasses="!py-1 !rounded-lg font-medium leading-5"
                        >
                          {ticket.SeatDetails}
                        </Badge>
                      </div>
                      {ticket.ShowTicketName ? (
                        <div className="flex w-full flex-none justify-between gap-x-8">
                          <TicketnameInput
                            ticketId={ticket.UniqueId}
                            ticketName={ticket.TicketName ?? ""}
                            isRequired={ticket.TicketNameRequired}
                          />
                        </div>
                      ) : (
                        <div className="w-[10rem]" />
                      )}
                    </div>
                  ))}
                </AccordionPrimitive.Content>
              </AccordionPrimitive.Item>
            </AccordionPrimitive.Root>
          </Card>
        ))
      ) : (
        <></>
      )}
      {nonComboTickets.length > 0 ? (
        nonComboTickets.map((ticket) => (
          <Card
            key={ticket.UniqueId}
            id={`ticket-${ticket.UniqueId}`}
            size="2"
            shopsiteClasses="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-2 px-4 bg-white rounded-xl border"
          >
            <div className="flex flex-col gap-1 w-full">
              <Text
                as="div"
                weight="bold"
                sharedClasses="flex items-center justify-between gap-2 leading-6"
                shopsiteClasses="font-bold text-black"
              >
                <span>{ticket.TicketType}</span>
                <DeleteTicket
                  eventName={eventName}
                  eventId={eventId}
                  priceAgeId={ticket.PriceAgeId}
                  showingId={showingId}
                  uniqueId={ticket.UniqueId}
                  type={ticket.TicketType}
                  analyticsConfigs={analyticsConfigs}
                  price={ticket.Cost}
                  minimum_quantity={ticket.PriceMinimum}
                  canTrack={canTrack}
                  is_last_ticket={
                    !hasOtherEvents
                      ? tickets_with_min[ticket.PriceId] &&
                        tickets_with_min[ticket.PriceId].length ===
                          tickets_with_min[ticket.PriceId][0].PriceMinimum
                        ? [...comboTickets, ...nonComboTickets].length ===
                          tickets_with_min[ticket.PriceId].length
                        : [...comboTickets, ...nonComboTickets].length === 1
                      : false
                  }
                  other_tickets_to_delete={
                    tickets_with_min[ticket.PriceId] &&
                    tickets_with_min[ticket.PriceId].length ===
                      tickets_with_min[ticket.PriceId][0].PriceMinimum
                      ? tickets_with_min[ticket.PriceId]
                          .map((ticket) => ticket.UniqueId)
                          .filter((unique_id) => unique_id !== ticket.UniqueId)
                      : undefined
                  }
                />
              </Text>
              <Text
                as="p"
                color="gray"
                size="2"
                shopsiteClasses="text-sm text-gray-500"
              >
                {formatPrice(locale, currency.CurrencyCode, ticket.Cost)}
              </Text>
              <Badge
                color="sky"
                sharedClasses="w-fit"
                shopsiteClasses="!py-1 !rounded-lg font-medium leading-5"
              >
                <Text size="2" weight="medium">
                  {ticket.SeatDetails}
                </Text>
              </Badge>
              {ticket.Discount ? (
                <Badge
                  color="green"
                  sharedClasses="w-fit"
                  shopsiteClasses="!py-1 !rounded-lg font-medium leading-5"
                >
                  <Text size="2" weight="medium">
                    {ticket.Discount}
                  </Text>
                </Badge>
              ) : (
                <></>
              )}
            </div>
            {"ConnectedTickets" in ticket &&
            ticket.ConnectedTickets.length > 0 ? (
              <div className="flex flex-col gap-1">
                <Text
                  as="span"
                  size="2"
                  weight="medium"
                  mt="2"
                  shopsiteClasses="font-medium text-black"
                >
                  {ticketTranslations("includedshowings")}
                </Text>
                <div className="flex items-center gap-1 flex-wrap">
                  {ticket.ConnectedTickets.map((connectedTicket) => (
                    <Badge
                      key={
                        connectedTicket.ShowingName ===
                        connectedTicket.ShowingDate
                          ? connectedTicket.ShowingDate
                          : `${connectedTicket.ShowingName} - ${connectedTicket.ShowingDate}`
                      }
                      sharedClasses="mt-1 w-fit"
                      shopsiteClasses="!py-1 !rounded-lg font-medium"
                    >
                      <Text
                        size="2"
                        weight="bold"
                        whitelabelClasses="whitespace-normal"
                      >
                        {connectedTicket.ShowingName ===
                        connectedTicket.ShowingDate
                          ? connectedTicket.ShowingDate
                          : `${connectedTicket.ShowingName} - ${connectedTicket.ShowingDate}`}
                      </Text>
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <></>
            )}
            {ticket.ShowTicketName ? (
              <div
                className={joinClassNames("w-full", isShopsite ? "" : "mt-4")}
              >
                <TicketnameInput
                  ticketId={ticket.UniqueId}
                  ticketName={ticket.TicketName ?? ""}
                  isRequired={ticket.TicketNameRequired}
                />
              </div>
            ) : (
              <div className="w-[10rem]" />
            )}
          </Card>
        ))
      ) : (
        <></>
      )}
    </ul>
  ) : (
    <div
      className={joinClassNames(
        "relative overflow-hidden mt-4 w-full border",
        isShopsite
          ? "rounded-lg text-gray-500"
          : "rounded-[var(--radius-3)] border-[color:var(--gray-5)]"
      )}
    >
      <div
        className={joinClassNames(
          "flex w-full items-start p-4 border-b",
          isShopsite
            ? "font-semibold text-sm text-black"
            : "border-[color:var(--gray-5)]"
        )}
      >
        <Text as="div" size="2" weight="bold" sharedClasses="flex-1">
          {ticketTranslations("heading")}
        </Text>
        <Text as="div" size="2" weight="bold" sharedClasses="flex-1">
          {ticketTranslations("price")}
        </Text>
        {(comboTickets.length > 0 && comboTickets[0].Items[0].ShowTicketName) ||
        (nonComboTickets.length > 0 && nonComboTickets[0].ShowTicketName) ? (
          <Text as="div" size="2" weight="bold" sharedClasses="flex-1 mr-6">
            {ticketTranslations("name")}
          </Text>
        ) : (
          <div className="flex-1 mr-6" />
        )}
      </div>
      <div
        className={joinClassNames(
          "divide-y",
          isShopsite ? "text-sm" : "divide-[color:var(--gray-5)]"
        )}
      >
        {comboTickets.length > 0 ? (
          comboTickets.map((comboTicket, index) => (
            <Accordion
              key={`${comboTicket.Combo}-${index}`}
              type="single"
              collapsible
              defaultValue={
                comboTicket.Items[0].TicketNameRequired
                  ? comboTicket.Combo
                  : undefined
              }
            >
              <AccordionItem
                value={comboTicket.Combo}
                className="!border-0 px-4 py-6"
              >
                <AccordionTrigger className="!p-0">
                  <div className="flex flex-1 items-center">
                    <div className="flex text-left w-1/3 flex-col">
                      <Text as="span" size="2" shopsiteClasses="text-black">
                        {comboTicket.Combo}
                      </Text>
                      <Badge
                        color="green"
                        sharedClasses="w-fit mt-1"
                        shopsiteClasses="!rounded-lg !py-1 font-medium"
                      >
                        <Text size="2" weight="bold">
                          {ticketTranslations("combos.heading")}
                        </Text>
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between flex-1 pr-px">
                      <div className="flex flex-col text-left">
                        <span>
                          {formatPrice(
                            locale,
                            currency.CurrencyCode,
                            handleTotalComboPrice(comboTicket.Items)
                          )}
                        </span>
                        <Badge
                          color="green"
                          sharedClasses="w-fit mt-1"
                          shopsiteClasses="!rounded-lg !py-1 font-medium"
                        >
                          <Text size="2" weight="bold">
                            {comboTicket.Items[0]?.Discount}
                          </Text>
                        </Badge>
                      </div>
                      <DeleteCombo
                        eventName={eventName}
                        eventId={eventId}
                        priceAgeId={comboTicket.Items[0].PriceAgeId}
                        showingId={showingId}
                        type={comboTicket.Items[0].TicketType}
                        price={comboTicket.Items[0].Cost}
                        analyticsConfigs={analyticsConfigs}
                        uniqueId={comboTicket.Items[0].UniqueId}
                        is_last_combo={
                          !hasOtherEvents
                            ? [...comboTickets, ...nonComboTickets].length === 1
                            : false
                        }
                      />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {comboTicket.Items.map((ticket) => (
                    <div
                      key={ticket.UniqueId}
                      id={`ticket-${ticket.UniqueId}`}
                      className="flex w-full items-center justify-between pt-6 pb-4 pr-6"
                    >
                      <div className="flex flex-col pl-4 w-1/3">
                        <Text
                          as="div"
                          sharedClasses="font-medium"
                          shopsiteClasses="text-black"
                        >
                          {ticket.TicketType}
                        </Text>
                        <Badge
                          color="sky"
                          sharedClasses="w-fit mt-1"
                          shopsiteClasses="!rounded-lg !py-1 font-medium"
                        >
                          <Text>{ticket.SeatDetails}</Text>
                        </Badge>
                      </div>
                      <div className="flex-1">
                        <span>
                          {formatPrice(
                            locale,
                            currency.CurrencyCode,
                            ticket.Cost
                          )}
                        </span>
                      </div>
                      {ticket.ShowTicketName ? (
                        <div className="flex items-center justify-between flex-1 gap-x-16 shrink">
                          <TicketnameInput
                            ticketId={ticket.UniqueId}
                            ticketName={ticket.TicketName ?? ""}
                            isRequired={ticket.TicketNameRequired}
                          />
                        </div>
                      ) : (
                        <div className="w-[10rem]" />
                      )}
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))
        ) : (
          <></>
        )}
        {nonComboTickets.length > 0 ? (
          nonComboTickets.map((ticket) => (
            <div
              key={ticket.UniqueId}
              id={`ticket-${ticket.UniqueId}`}
              className={ticket.TicketNameRequired ? "px-4 py-7" : "p-4"}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex flex-1 items-center">
                  <div className="flex flex-col pr-2">
                    <Text
                      as="span"
                      size="2"
                      weight="medium"
                      shopsiteClasses="font-medium text-black"
                    >
                      {ticket.TicketType}
                    </Text>
                    <Badge
                      color="sky"
                      sharedClasses="mt-1 w-fit"
                      shopsiteClasses="!py-1 !rounded-lg font-medium"
                    >
                      <Text
                        size="2"
                        weight="bold"
                        whitelabelClasses="whitespace-normal"
                      >
                        {ticket.SeatDetails}
                      </Text>
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <Text as="span" color="gray" size="2">
                    {formatPrice(locale, currency.CurrencyCode, ticket.Cost)}
                  </Text>
                  {ticket.Discount ? (
                    <Badge
                      color="green"
                      sharedClasses="w-fit"
                      shopsiteClasses="!py-1 !rounded-lg font-medium"
                    >
                      <Text size="2" weight="bold">
                        {ticket.Discount}
                      </Text>
                    </Badge>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="flex items-center justify-between flex-1 gap-x-2 mr-[calc(1.5rem+1px)] shrink">
                  {ticket.ShowTicketName ? (
                    <TicketnameInput
                      ticketId={ticket.UniqueId}
                      ticketName={ticket.TicketName ?? ""}
                      isRequired={ticket.TicketNameRequired}
                    />
                  ) : (
                    <div className="w-[10rem]" />
                  )}
                  <DeleteTicket
                    eventName={eventName}
                    eventId={eventId}
                    priceAgeId={ticket.PriceAgeId}
                    showingId={showingId}
                    uniqueId={ticket.UniqueId}
                    type={ticket.TicketType}
                    analyticsConfigs={analyticsConfigs}
                    price={ticket.Cost}
                    minimum_quantity={ticket.PriceMinimum}
                    canTrack={canTrack}
                    is_last_ticket={
                      !hasOtherEvents
                        ? tickets_with_min[ticket.PriceId] &&
                          tickets_with_min[ticket.PriceId].length ===
                            tickets_with_min[ticket.PriceId][0].PriceMinimum
                          ? [...comboTickets, ...nonComboTickets].length ===
                            tickets_with_min[ticket.PriceId].length
                          : [...comboTickets, ...nonComboTickets].length === 1
                        : false
                    }
                    other_tickets_to_delete={
                      tickets_with_min[ticket.PriceId] &&
                      tickets_with_min[ticket.PriceId].length ===
                        tickets_with_min[ticket.PriceId][0].PriceMinimum
                        ? tickets_with_min[ticket.PriceId]
                            .map((ticket) => ticket.UniqueId)
                            .filter(
                              (unique_id) => unique_id !== ticket.UniqueId
                            )
                        : undefined
                    }
                  />
                </div>
              </div>
              {"ConnectedTickets" in ticket &&
              ticket.ConnectedTickets.length > 0 ? (
                <div className="flex items-center gap-2 mt-2">
                  <Text
                    as="span"
                    size="2"
                    weight="medium"
                    shopsiteClasses="font-medium text-black"
                  >
                    {ticketTranslations("includedshowings")}
                  </Text>
                  <div className="flex items-center gap-1 flex-wrap">
                    {ticket.ConnectedTickets.map((connectedTicket) => (
                      <Badge
                        key={
                          connectedTicket.ShowingName ===
                          connectedTicket.ShowingDate
                            ? connectedTicket.ShowingDate
                            : `${connectedTicket.ShowingName} - ${connectedTicket.ShowingDate}`
                        }
                        sharedClasses="mt-1 w-fit"
                        shopsiteClasses="!py-1 !rounded-lg font-medium"
                      >
                        <Text
                          size="2"
                          weight="bold"
                          whitelabelClasses="whitespace-normal"
                        >
                          {connectedTicket.ShowingName ===
                          connectedTicket.ShowingDate
                            ? connectedTicket.ShowingDate
                            : `${connectedTicket.ShowingName} - ${connectedTicket.ShowingDate}`}
                        </Text>
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          ))
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default DisplayTickets;
